import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CreditCard, CheckCircle2, AlertCircle, Loader, ExternalLink, Zap, DollarSign, Clock } from "lucide-react";

export default function StripeConnectPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [showPayout, setShowPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/stripe/connect/status");
      setStatus(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const startOnboarding = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/stripe/connect/onboarding");
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/stripe/connect/dashboard-link");
      window.open(data.url, "_blank");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const instantPayout = async () => {
    const cents = Math.floor(parseFloat(payoutAmount || "0") * 100);
    if (cents <= 0) { setErr("Enter an amount to withdraw"); return; }
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/stripe/connect/instant-payout", { amount_cents: cents });
      setMsg(`Instant payout initiated: $${(data.amount_cents / 100).toFixed(2)} (${data.status}). Funds usually arrive in ~30 minutes.`);
      setPayoutAmount("");
      setShowPayout(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 8000);
    }
  };

  if (!status) return (
    <div className="bg-asap-blue/30 backdrop-blur border-2 border-asap-yellow/40 p-4 text-center text-white/70 text-sm">
      <Loader className="animate-spin inline mr-2" size={14}/> Checking Stripe status…
    </div>
  );

  const notConnected = !status.connected;
  const restricted = status.connected && (!status.charges_enabled || !status.payouts_enabled);
  const ready = status.charges_enabled && status.payouts_enabled;
  const instantAvail = (status.instant_available_cents || 0) / 100;
  const available = (status.available_cents || 0) / 100;

  return (
    <div className="bg-asap-blue/30 backdrop-blur border-2 border-asap-yellow/40 p-4" data-testid="stripe-connect-panel">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard className="text-asap-yellow" size={16}/>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-asap-yellow">Instant Payouts</div>
        {ready && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-emerald-500 text-white"><CheckCircle2 size={10}/> READY</span>}
        {restricted && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-amber-500 text-white"><AlertCircle size={10}/> ACTION NEEDED</span>}
        {notConnected && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-slate-500 text-white">NOT SET UP</span>}
      </div>

      {msg && <div className="mb-2 p-2 bg-emerald-500/20 border border-emerald-500 text-emerald-100 text-xs">{msg}</div>}
      {err && <div className="mb-2 p-2 bg-red-500/20 border border-asap-red text-red-100 text-xs">{err}</div>}

      {notConnected && (
        <>
          <p className="text-white/80 text-xs mb-3">Get paid same-day per job. Stripe handles ID + bank verification. It takes ~3 minutes.</p>
          <button onClick={startOnboarding} disabled={busy} data-testid="stripe-onboard-btn" className="w-full px-3 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-xs disabled:opacity-50 inline-flex items-center justify-center gap-1">
            {busy ? <Loader size={12} className="animate-spin"/> : <Zap size={12}/>} Set Up Stripe Payouts
          </button>
        </>
      )}

      {restricted && (
        <>
          <p className="text-white/80 text-xs mb-2">Stripe needs more info. This is normal — sometimes they need a photo of your ID or a bank account update.</p>
          {status.requirements_currently_due?.length > 0 && (
            <div className="text-[10px] text-asap-yellow mb-2 font-bold">Still needed: {status.requirements_currently_due.join(", ")}</div>
          )}
          <button onClick={startOnboarding} disabled={busy} data-testid="stripe-continue-btn" className="w-full px-3 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-xs disabled:opacity-50 inline-flex items-center justify-center gap-1">
            {busy ? <Loader size={12} className="animate-spin"/> : <AlertCircle size={12}/>} Continue Onboarding
          </button>
        </>
      )}

      {ready && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-black/30 p-2 border border-asap-yellow/30">
              <div className="text-[9px] uppercase tracking-widest text-asap-yellow/80 font-bold flex items-center gap-1"><Zap size={9}/> Instant Cash</div>
              <div className="font-display text-lg text-asap-yellow" data-testid="instant-available">${instantAvail.toFixed(2)}</div>
            </div>
            <div className="bg-black/30 p-2 border border-white/20">
              <div className="text-[9px] uppercase tracking-widest text-white/60 font-bold flex items-center gap-1"><Clock size={9}/> Bank (2 day)</div>
              <div className="font-display text-lg text-white" data-testid="available">${available.toFixed(2)}</div>
            </div>
          </div>

          {!showPayout ? (
            <div className="space-y-2">
              <button onClick={() => setShowPayout(true)} disabled={instantAvail <= 0 || busy} data-testid="stripe-payout-btn" className="w-full px-3 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-xs disabled:opacity-40 inline-flex items-center justify-center gap-1">
                <Zap size={12}/> Cash Out Instantly
              </button>
              <button onClick={openDashboard} disabled={busy} data-testid="stripe-dashboard-btn" className="w-full px-3 py-1.5 border-2 border-asap-yellow/60 text-asap-yellow font-bold uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1 hover:bg-asap-yellow/10">
                <ExternalLink size={10}/> Stripe Dashboard
              </button>
            </div>
          ) : (
            <div className="space-y-2 border-t border-asap-yellow/30 pt-2">
              <div className="text-[10px] uppercase tracking-widest text-asap-yellow font-bold">Amount to withdraw</div>
              <div className="flex gap-1">
                <div className="flex-1 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-asap-yellow font-bold">$</span>
                  <input type="number" step="0.01" min="0" max={instantAvail} value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} data-testid="payout-amount" placeholder={instantAvail.toFixed(2)} className="w-full pl-6 pr-2 py-2 bg-white/10 border border-asap-yellow/50 text-white text-sm outline-none"/>
                </div>
                <button onClick={() => setPayoutAmount(instantAvail.toFixed(2))} data-testid="payout-max" className="px-2 text-[10px] uppercase tracking-widest font-bold border border-asap-yellow/60 text-asap-yellow">Max</button>
              </div>
              <div className="text-[9px] text-white/60">Stripe fee: ~1% of withdrawal. Arrives in ~30 min.</div>
              <div className="flex gap-2">
                <button onClick={() => { setShowPayout(false); setPayoutAmount(""); }} className="flex-1 px-2 py-1.5 border border-white/40 text-white/70 text-[10px] uppercase tracking-widest font-bold">Cancel</button>
                <button onClick={instantPayout} disabled={busy || !payoutAmount} data-testid="payout-confirm" className="flex-1 px-2 py-1.5 bg-asap-yellow text-asap-deep font-display uppercase text-xs disabled:opacity-50 inline-flex items-center justify-center gap-1">
                  {busy ? <Loader size={10} className="animate-spin"/> : <DollarSign size={10}/>} Confirm Payout
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
